import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import "../assets/styles/CreateTowerPage.css";
import { useDispatch, useSelector } from "react-redux";
import { setTowerType, selectTowerType } from "../redux/slices/towerSlice";
import { PROJECT_FORM_MAPPING } from "../config/projectFormMapping";
import DynamicForm from "../components/DynamicForm";
import apiService from "../services/apiService";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../hooks/useToast";
import { MESSAGE } from "../constants/constant";
import SerialNumberModal from "../components/modals/SerialNumberModal";
import EditNumber from "../components/EditNumber";
import { useAuth } from "../context/AuthContext";
import {
  matchingFields,
  matchingFieldsGTD,
  matchingFieldsGTD_Ignore,
  updateMatchingFields,
} from "../utils/matchingFields";
import { useInteractiveTooltips } from "../hooks/useInteractiveTooltips";

const CreateTowerPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const currentTowerType = useSelector(selectTowerType);
  const [formAnswers, setFormAnswers] = useState({
    tower: {
      type: "",
      subtype: "",
    },
  });
  const [initialData, setInitialData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [showSubtype] = useState(true);
  const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
  const [edit, setEdit] = useState(false);
  const [serialNumber, setSerialNumber] = useState("");
  const existingSerialNumber = serialNumber !== "";
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [hasWarningShown, setHasWarningShown] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [previousStatus, setPreviousStatus] = useState(
    location.state?.initialData?.status || ""
  );

  const { user } = useAuth();

  useInteractiveTooltips(
    {
      placement: "top",
      html: true,
      delay: { show: 100, hide: 100 }, // Add delay to tooltip showing/hiding
    },
    [formAnswers, invalidFields, validationAttempted, showSubtype]
  );

  const removeEmptyValues = (obj) => {
    if (!obj) return {};
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => {
        if (value === null || value === undefined || value === "") return false;
        if (typeof value === "string" && value.trim() === "") return false;
        return true;
      })
    );
  };

  const validateForm = (answers) => {
    const emptyFields = [];

    PROJECT_FORM_MAPPING.generalInfo.forEach((mapping) => {
      // Skip validation if field is tower-type specific and doesn't match current tower
      if (
        mapping.questionId.towerType &&
        mapping.questionId.towerType !== answers.tower.type
      ) {
        return;
      }

      const componentId = mapping.componentId;
      const fieldId = Array.isArray(mapping.questionId)
        ? mapping.questionId.find((q) => q.towerType === answers.tower.type)?.id
        : mapping.questionId;

      console.log("validate:", componentId, fieldId, answers);

      // Skip certain conditional validations
      if (
        componentId === "distribution_system" &&
        !(
          answers.tower.type === "counterflow" &&
          answers.tower.subtype === "back_to_back"
        )
      ) {
        return;
      }

      if (componentId === "louvers") {
        if (
          (answers.tower.type === "crossflow" &&
            answers.louvers.louver_exists_xf === "no") ||
          (answers.tower.type === "counterflow" &&
            answers.louvers.louver_exists_cf === "no")
        ) {
          return;
        }
        //skip louver validations if louver does not exist
        if (
          fieldId.includes("louver_") &&
          answers.louvers.louver_exists === "no"
        ) {
          return;
        }
      }

      if (componentId === "notes") {
        return;
      } // Skip partitionwall validations if none is selected
      if (
        componentId === "partitionwall" &&
        ((answers.tower.type === "crossflow" &&
          answers.partitionwall?.partitionwall_type_xf === "none") ||
          (answers.tower.type === "counterflow" &&
            answers.partitionwall?.partitionwall_type_cf === "none"))
      ) {
        return;
      }

      // Skip partitionwall_frp_type validation if material is not frp
      if (
        componentId === "partitionwall" &&
        fieldId === "partitionwall_frp_type" &&
        answers.partitionwall?.partitionwall_material !== "frp"
      ) {
        return;
      } // Skip partitionwall_wood_type validation if material is not wood
      if (
        componentId === "partitionwall" &&
        fieldId === "partitionwall_wood_type" &&
        answers.partitionwall?.partitionwall_material !== "wood"
      ) {
        return;
      }

      // Skip partitionwall_between_cells validation if no partition wall
      if (
        componentId === "partitionwall" &&
        fieldId === "partitionwall_between_cells" &&
        ((answers.tower.type === "crossflow" &&
          answers.partitionwall?.partitionwall_type_xf === "none") ||
          (answers.tower.type === "counterflow" &&
            answers.partitionwall?.partitionwall_type_cf === "none"))
      ) {
        return;
      }
      // Skip partitionwall_panels_both_sides validation if no partition wall
      if (
        componentId === "partitionwall" &&
        fieldId === "partitionwall_panels_both_sides" &&
        ((answers.tower.type === "crossflow" &&
          answers.partitionwall?.partitionwall_type_xf === "none") ||
          (answers.tower.type === "counterflow" &&
            answers.partitionwall?.partitionwall_type_cf === "none"))
      ) {
        return;
      }

      // Skip firewall fields validation if firewall doesn't exist
      if (
        componentId === "firewall" &&
        fieldId !== "firewall_exists" &&
        answers.firewall?.firewall_exists !== "yes"
      ) {
        return;
      }

      // Skip firewall_frp_thickness validation if material is not frp
      if (
        componentId === "firewall" &&
        fieldId === "firewall_frp_thickness" &&
        answers.firewall?.firewall_material !== "frp"
      ) {
        return;
      }

      // Skip firewall_cement_board_thickness validation if material is not cement_board
      if (
        componentId === "firewall" &&
        fieldId === "firewall_cement_board_thickness" &&
        answers.firewall?.firewall_material !== "cement_board"
      ) {
        return;
      }

      // Skip firewall_quantity validation if firewall_between_cells is not "no"
      if (
        componentId === "firewall" &&
        fieldId === "firewall_quantity" &&
        answers.firewall?.firewall_between_cells !== "no"
      ) {
        return;
      }

      const value = answers[componentId]?.[fieldId];

      if (
        answers.generalInfo.status !== "Rejected" &&
        fieldId === "statusReason"
      )
        return;
      if (
        answers.tower.type === "crossflow" &&
        fieldId === "casing_air_inlets_xf" &&
        (value === undefined || value === null || value === "")
      )
        return;

      // Check if field value exists in answers object
      if (value === undefined || value === null || value === "") {
        emptyFields.push(`${componentId}.${fieldId}`);
      }

      if (value === "other") {
        let otherFieldId;
        if (
          fieldId === "baySizeHeight" ||
          fieldId === "baySizeLength" ||
          fieldId === "baySizeWidth"
        ) {
          otherFieldId = `${fieldId}Other`;
        } else {
          // Handle the regular pattern
          otherFieldId = fieldId.match(/_(xf|cf)$/)
            ? fieldId.replace(/_(xf|cf)$/, "_other_$1")
            : `${fieldId}_other`;
        }
        const otherValue = answers[componentId]?.[otherFieldId];

        if (!otherValue) {
          emptyFields.push(`${componentId}.${otherFieldId}`);
        }
      }
    });

    // Special case for dealId
    const dealIdIndex = emptyFields.indexOf("generalInfo.dealId");
    if (dealIdIndex !== -1 && answers.companyInfo?.company_name) {
      emptyFields.splice(dealIdIndex, 1);
    }

    return emptyFields;
  };

  useEffect(() => {
    if (
      !isUpdateMode &&
      !existingSerialNumber &&
      !location.state?.skipModal &&
      location.state?.initialData &&
      !location.state?.isUpdate
    ) {
      setShowSerialNumberModal(true);
    }
  }, [isUpdateMode, existingSerialNumber, location.state]);

  useEffect(() => {
    if (serialNumber) {
      setFormAnswers((prev) => ({
        ...prev,
        generalInfo: {
          ...prev.generalInfo,
          serialNumber: serialNumber,
          submitter: user?.name || "Guest",
          submitter_email: user?.email || "",
        },
      }));
    }
  }, [serialNumber]);

  useEffect(() => {
    if (!formAnswers.generalInfo?.submitter) {
      setFormAnswers((prev) => ({
        ...prev,
        generalInfo: {
          ...prev.generalInfo,
          submitter: user?.name || "Guest",
          submitter_email: user?.email || "",
        },
      }));
    } else if (!formAnswers.generalInfo?.submitter_email) {
      setFormAnswers((prev) => ({
        ...prev,
        generalInfo: {
          ...prev.generalInfo,
          submitter_email: user?.email || "",
        },
      }));
    }
  }, []);

  useEffect(() => {
    if (location.state?.initialData) {
      setIsUpdateMode(location.state.isUpdate);
      setInitialData(location.state.initialData);
      const existingData = location.state.initialData;
      console.log("Received existingData data:", existingData);

      const towerType = existingData.towerInfo?.towerType?.toLowerCase() || "";

      if (towerType) dispatch(setTowerType(towerType));

      let t_company_name = existingData.towerInfo?.companyInfo?.company_name;
      if (!t_company_name && existingData.dealId) {
        (async () => {
          try {
            let response = await apiService.get(
              `/opportunity/getOpportunity/${existingData.dealId}`
            );
            if (response.status === 200) {
              t_company_name = response.data.data.data.Account_Name.name;
              setFormAnswers((prevState) => ({
                ...prevState,
                companyInfo: {
                  ...prevState.companyInfo,
                  company_name: t_company_name,
                },
              }));
            }
          } catch (err) {
            console.log(err);
          }
        })();
      }

      if (location.state.isUpdate) {
        let newFormState = {
          generalInfo: {
            dealId: location.state.dealId || "",
            serialNumber:
              existingData.towerInfo?.generalTowerDescription?.serialNumber ||
              "",
            status: existingData.status || "Pending",
            customer_tower_designation:
              existingData.towerInfo?.generalTowerDescription
                ?.customer_tower_designation || "",
            submitter:
              existingData.towerInfo?.generalTowerDescription?.submitter || "",
            submitter_email:
              existingData.towerInfo?.generalTowerDescription
                ?.submitter_email ||
              user?.email ||
              "",
            model: existingData.towerInfo?.generalTowerDescription?.model || "",
            state: existingData.towerInfo?.generalTowerDescription?.state || "",
            city: existingData.towerInfo?.generalTowerDescription?.city || "",
            street:
              existingData.towerInfo?.generalTowerDescription?.street || "",
            zip: existingData.towerInfo?.generalTowerDescription?.zip || "",
            numberOfCells:
              existingData.towerInfo?.generalTowerDescription?.numberOfCells ||
              null,
            cellLength:
              existingData.towerInfo?.generalTowerDescription?.cellLength || "",
            cellWidth:
              existingData.towerInfo?.generalTowerDescription?.cellWidth || "",
            cellHeight:
              existingData.towerInfo?.generalTowerDescription?.cellHeight || "",
            baySizeLength:
              existingData.towerInfo?.scopeOfWork?.fandeckSow
                ?.sow_fandeck_bay_length || "",
            baySizeLengthOther:
              existingData.towerInfo?.scopeOfWork?.fandeckSow
                ?.sow_fandeck_bay_length_other || "",
            baySizeWidth:
              existingData.towerInfo?.scopeOfWork?.fandeckSow
                ?.sow_fandeck_bay_width || "",
            baySizeWidthOther:
              existingData.towerInfo?.scopeOfWork?.fandeckSow
                ?.sow_fandeck_bay_width_other || "",
            baySizeHeight:
              existingData.towerInfo?.scopeOfWork?.structureSow
                ?.sow_structure_bay_size_height || "",
            baySizeHeightOther:
              existingData.towerInfo?.scopeOfWork?.structureSow
                ?.sow_structure_bay_size_height_other || "",
            airInletHeight:
              existingData.towerInfo?.generalTowerDescription?.airInletHeight ||
              "",
            ...(existingData.status === "Rejected" &&
              existingData.towerInfo?.generalTowerDescription?.statusReason && {
                statusReason:
                  existingData.towerInfo.generalTowerDescription.statusReason,
              }),
          },
          companyInfo: {
            company_name: t_company_name || "",
          },
          tower: {
            type: towerType,
            subtype:
              existingData.towerInfo?.generalTowerDescription?.towerSubtype ||
              "",
            distribution_system:
              existingData.towerInfo?.components?.distributionSystem
                ?.dist_cell_arrangement_cf || "",
          },
          towerManufacturer: {
            tower_manufacturer:
              existingData.towerInfo?.towerManufacturer?.tower_manufacturer ||
              "",
            tower_manufacturer_other:
              existingData.towerInfo?.towerManufacturer
                ?.tower_manufacturer_other || "",
          },
          casing: {
            casing_air_inlets:
              existingData.towerInfo?.components?.casing?.casing_air_inlets ||
              "",
            casing_air_inlets_cf:
              existingData.towerInfo?.components?.casing
                ?.casing_air_inlets_cf || "",
            casing_air_inlets_xf:
              existingData.towerInfo?.components?.casing
                ?.casing_air_inlets_xf || "",
            casing_material:
              existingData.towerInfo?.components?.casing?.casing_material ||
              existingData.towerInfo?.generalTowerDescription
                ?.casing_material ||
              "",
            casing_material_cf:
              existingData.towerInfo?.components?.casing?.casing_material_cf ||
              "",
            casing_material_xf:
              existingData.towerInfo?.components?.casing?.casing_material_xf ||
              "",
            casing_material_other:
              existingData.towerInfo?.components?.casing
                ?.casing_material_other || "",
          },
          coldWaterBasin: {
            cwb_grade_to_curb_height:
              existingData.towerInfo?.components?.coldWaterBasin
                ?.cwb_grade_to_curb_height || "",
          },
          distributionSystem: {
            dist_header_cl_cwb_curb:
              existingData.towerInfo?.components?.distributionSystem
                ?.dist_header_cl_cwb_curb || "",
          },
          ladders: {
            ladder_count:
              existingData.towerInfo?.components?.ladders?.ladder_count || "",
            ladder_count_other:
              existingData.towerInfo?.components?.ladders?.ladder_count_other ||
              "",
          },
          fanstack: {
            fanstack_diameter:
              existingData.towerInfo?.components?.fanstack?.fanstack_diameter ||
              "",
            fanstack_diameter_other:
              existingData.towerInfo?.components?.fanstack
                ?.fanstack_diameter_other || "",
            fanstack_height:
              existingData.towerInfo?.components?.fanstack?.fanstack_height ||
              "",
            fanstack_height_other:
              existingData.towerInfo?.components?.fanstack
                ?.fanstack_height_other || "",
          },
          fill: {
            fill_type:
              existingData.towerInfo?.components?.fill?.fill_type || "",
            fill_type_xf:
              existingData.towerInfo?.components?.fill?.fill_type_xf || "",
            fill_type_cf:
              existingData.towerInfo?.components?.fill?.fill_type_cf || "",
            fill_type_other:
              existingData.towerInfo?.components?.fill?.fill_type_other || "",
            fill_type_other_xf:
              existingData.towerInfo?.components?.fill?.fill_type_other_xf ||
              "",
            fill_type_other_cf:
              existingData.towerInfo?.components?.fill?.fill_type_other_cf ||
              "",
            fill_orientation:
              existingData.towerInfo?.components?.fill?.fill_orientation || "",
            fill_orientation_xf:
              existingData.towerInfo?.components?.fill?.fill_orientation_xf ||
              "",
            fill_orientation_cf:
              existingData.towerInfo?.components?.fill?.fill_orientation_cf ||
              "",
          },
          louvers: {
            louver_exists:
              existingData.towerInfo?.components?.louvers?.louver_exists || "",
            louver_exists_xf:
              existingData.towerInfo?.components?.louvers?.louver_exists_xf ||
              "",
            louver_exists_cf:
              existingData.towerInfo?.components?.louvers?.louver_exists_cf ||
              "",
            louver_row_count:
              existingData.towerInfo?.components?.louvers?.louver_row_count ||
              "",
            louver_row_count_xf:
              existingData.towerInfo?.components?.louvers
                ?.louver_row_count_xf || "",
            louver_row_count_cf:
              existingData.towerInfo?.components?.louvers
                ?.louver_row_count_cf || "",
            louver_spacing:
              existingData.towerInfo?.components?.louvers?.louver_spacing || "",
            louver_spacing_xf:
              existingData.towerInfo?.components?.louvers?.louver_spacing_xf ||
              "",
            louver_spacing_cf:
              existingData.towerInfo?.components?.louvers?.louver_spacing_cf ||
              "",
            louver_blade_width:
              existingData.towerInfo?.components?.louvers?.louver_blade_width ||
              "",
            louver_blade_width_xf:
              existingData.towerInfo?.components?.louvers
                ?.louver_blade_width_xf || "",
            louver_blade_width_cf:
              existingData.towerInfo?.components?.louvers
                ?.louver_blade_width_cf || "",
            louver_blade_width_other:
              existingData.towerInfo?.components?.louvers
                ?.louver_blade_width_other || "",
            louver_blade_width_other_xf:
              existingData.towerInfo?.components?.louvers
                ?.louver_blade_width_other_xf || "",
            louver_blade_width_other_cf:
              existingData.towerInfo?.components?.louvers
                ?.louver_blade_width_other_cf || "",
            louver_material:
              existingData.towerInfo?.components?.louvers?.louver_material ||
              existingData.towerInfo?.generalTowerDescription
                ?.louver_material ||
              "",
            louver_material_xf:
              existingData.towerInfo?.components?.louvers?.louver_material_xf ||
              "",
            louver_material_cf:
              existingData.towerInfo?.components?.louvers?.louver_material_cf ||
              "",
            louver_material_other:
              existingData.towerInfo?.components?.louvers
                ?.louver_material_other || "",
          },
          mechanical: {
            motor_hp:
              existingData.towerInfo?.components?.mechanical?.motor_hp || "",
            fan_diameter:
              existingData.towerInfo?.components?.mechanical?.fan_diameter ||
              "",
            fan_diameter_other:
              existingData.towerInfo?.components?.mechanical
                ?.fan_diameter_other || "",
          },
          stairway: {
            stairway_count:
              existingData.towerInfo?.components?.stairway?.stairway_count ||
              "",
            stairway_count_other:
              existingData.towerInfo?.components?.stairway
                ?.stairway_count_other || "",
          },
          structure: {
            structure_material:
              existingData.towerInfo?.components?.structure
                ?.structure_material || "",
          },
          partitionwall: {
            partitionwall_type:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type || "",
            partitionwall_type_cf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_cf || "",
            partitionwall_type_xf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_xf || "",
            partitionwall_type_other_cf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_other_cf || "",
            partitionwall_type_other_xf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_other_xf || "",
            partitionwall_material:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_material || "",
            partitionwall_material_other:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_material_other || "",
            partitionwall_frp_type:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_frp_type || "",
            partitionwall_wood_type:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_wood_type || "",
            partitionwall_quantity:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_quantity || "",
            partitionwall_panels_both_sides:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_panels_both_sides || "",
            partitionwall_between_cells:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_between_cells || "",
          },
          firewall: {
            firewall_exists:
              existingData.towerInfo?.components?.firewall?.firewall_exists ||
              "",
            firewall_material:
              existingData.towerInfo?.components?.firewall?.firewall_material ||
              "",
            firewall_frp_thickness:
              existingData.towerInfo?.components?.firewall
                ?.firewall_frp_thickness || "",
            firewall_cement_board_thickness:
              existingData.towerInfo?.components?.firewall
                ?.firewall_cement_board_thickness || "",
            firewall_between_cells:
              existingData.towerInfo?.components?.firewall
                ?.firewall_between_cells || "",
            firewall_quantity:
              existingData.towerInfo?.components?.firewall?.firewall_quantity ||
              "",
            firewall_material_other:
              existingData.towerInfo?.components?.firewall
                ?.firewall_material_other || "",
          },
          notes: {
            tower_notes:
              existingData.towerInfo?.generalTowerDescription?.tower_notes ||
              "",
          },
        };
        // if (towerType === "crossflow") {
        //   newFormState.louvers.louver_exists_xf =
        //     existingData.towerInfo?.components?.louvers?.exists || "no";
        //   newFormState.louvers.louver_blade_width_xf =
        //     existingData.towerInfo?.components?.louvers
        //       ?.louver_blade_width_xf || "";
        //   newFormState.fill.xf_fill_type =
        //     existingData.towerInfo?.components?.fill?.fill_type || "";
        // } else if (towerType === "counterflow") {
        //   newFormState.louvers.louver_exists_cf =
        //     existingData.towerInfo?.components?.louvers?.exists || "no";
        //   newFormState.louvers.louver_blade_width_cf =
        //     existingData.towerInfo?.components?.louvers
        //       ?.louver_blade_width_cf || "";
        //   newFormState.fill.cf_fill_type =
        //     existingData.towerInfo?.components?.fill?.fill_type || "";
        // }
        if (
          newFormState.stairway.stairway_count !== "Other" &&
          newFormState.stairway.stairway_count > 2
        ) {
          newFormState.stairway.stairway_count_other =
            newFormState.stairway.stairway_count;
          newFormState.stairway.stairway_count = "Other";
        }

        setFormAnswers(newFormState);
        console.log("Setting form answers to:", newFormState);
      } else {
        setFormAnswers({
          generalInfo: {
            dealId: location.state.dealId || "",
            serialNumber:
              existingData.towerInfo?.generalTowerDescription?.serialNumber ||
              "",
            status: existingData.status || "Pending",
            statusReason:
              existingData.towerInfo?.generalTowerDescription?.statusReason ||
              "",
            customer_tower_designation:
              existingData.towerInfo?.generalTowerDescription
                ?.customer_tower_designation || "",
            submitter:
              existingData.towerInfo?.generalTowerDescription?.submitter || "",
            submitter_email:
              existingData.towerInfo?.generalTowerDescription
                ?.submitter_email ||
              user?.email ||
              "",
            model: existingData.towerInfo?.generalTowerDescription?.model || "",
            state: existingData.towerInfo?.generalTowerDescription?.state || "",
            city: existingData.towerInfo?.generalTowerDescription?.city || "",
            street:
              existingData.towerInfo?.generalTowerDescription?.street || "",
            zip: existingData.towerInfo?.generalTowerDescription?.zip || "",
            numberOfCells:
              existingData.towerInfo?.generalTowerDescription?.numberOfCells ||
              null,
            cellLength:
              existingData.towerInfo?.generalTowerDescription?.cellLength || "",
            cellWidth:
              existingData.towerInfo?.generalTowerDescription?.cellWidth || "",
            cellHeight:
              existingData.towerInfo?.generalTowerDescription?.cellHeight || "",
            baySizeLength:
              existingData.towerInfo?.generalTowerDescription?.baySizeLength ||
              "",
            baySizeWidth:
              existingData.towerInfo?.generalTowerDescription?.baySizeWidth ||
              "",
            baySizeHeight:
              existingData.towerInfo?.generalTowerDescription?.baySizeHeight ||
              "",
            airInletHeight:
              existingData.towerInfo?.generalTowerDescription?.airInletHeight ||
              "",
          },
          companyInfo: {
            ...existingData.towerInfo?.companyInfo, //this may have a company name already defined
            company_name: t_company_name || "",
          },
          tower: {},
          towerManufacturer: {},
          coldWaterBasin: {},
          distributionSystem: {},
          ladders: {},
          fanstack: {},
          fill: {},
          louvers: {},
          mechanical: {},
          stairway: {},
          structure: {},
          partitionwall: {
            partitionwall_type:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type || "",
            partitionwall_type_cf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_cf || "",
            partitionwall_type_xf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_xf || "",
            partitionwall_type_other_cf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_other_cf || "",
            partitionwall_type_other_xf:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_type_other_xf || "",
            partitionwall_material:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_material || "",
            partitionwall_material_other:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_material_other || "",
            partitionwall_frp_type:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_frp_type || "",
            partitionwall_wood_type:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_wood_type || "",
            partitionwall_quantity:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_quantity || "",
            partitionwall_panels_both_sides:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_panels_both_sides || "",
            partitionwall_between_cells:
              existingData.towerInfo?.components?.partitionwall
                ?.partitionwall_between_cells || "",
          },
          firewall: {
            firewall_exists:
              existingData.towerInfo?.components?.firewall?.firewall_exists ||
              "",
            firewall_material:
              existingData.towerInfo?.components?.firewall?.firewall_material ||
              "",
            firewall_frp_thickness:
              existingData.towerInfo?.components?.firewall
                ?.firewall_frp_thickness || "",
            firewall_cement_board_thickness:
              existingData.towerInfo?.components?.firewall
                ?.firewall_cement_board_thickness || "",
            firewall_between_cells:
              existingData.towerInfo?.components?.firewall
                ?.firewall_between_cells || "",
            firewall_quantity:
              existingData.towerInfo?.components?.firewall?.firewall_quantity ||
              "",
            firewall_material_other:
              existingData.towerInfo?.components?.firewall
                ?.firewall_material_other || "",
          },
          notes: {
            tower_notes:
              existingData.towerInfo?.generalTowerDescription?.tower_notes ||
              "",
          },
        });
      }
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    console.log("Form answers updated:", formAnswers);
  }, [formAnswers]);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      const answers = await formAnswers;
      const dealId = answers.generalInfo?.dealId;

      if (dealId) {
        try {
          let response = await apiService.get(
            `/opportunity/getOpportunity/${dealId}`
          );
          if (response.status === 200) {
            const companyName = response.data.data.data.Account_Name.name;
            if (answers.companyInfo?.company_name === companyName) return;

            setFormAnswers((prev) => ({
              ...prev,
              companyInfo: {
                ...prev.companyInfo,
                company_name: companyName,
              },
            }));
          }
        } catch (err) {
          console.log(err);
        }
      }
    };

    fetchCompanyInfo();
  }, [formAnswers.generalInfo?.dealId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationAttempted(true);
    const emptyFields = validateForm(formAnswers);
    setInvalidFields(emptyFields);
    console.log("formAnswers:", formAnswers);
    console.log("emptyFields:", emptyFields);

    if (emptyFields.length > 0 && !hasWarningShown) {
      console.log("emptyFields", emptyFields);
      setValidationAttempted(true);
      setHasWarningShown(true);
      addToast(
        "Please fill out all required fields. Press Update Tower to continue regardless.",
        "danger"
      );
      return;
    }

    if (!formAnswers.generalInfo.status) {
      invalidFields.push("status");
      addToast("Status is required.", "danger");
      return;
    }

    if (
      !formAnswers.generalInfo.statusReason &&
      formAnswers.generalInfo.status === "Rejected"
    ) {
      invalidFields.push("statusReason");
      addToast("Status Reason is required.", "danger");
      return;
    }

    setIsSubmitting(true);

    const getFillTypeValue = () => {
      if (formAnswers.fill?.fill_type?.includes("other")) {
        const otherKey = "fill_type_other";
        return formAnswers.fill?.fill_type_other || "";
      }
      return formAnswers.fill?.fill_type || "";
    };

    try {
      const getOtherValue = (answers, baseKey) => {
        // Check if answers exists
        if (!answers) return "";
        
        if (
          typeof answers[baseKey] === "string" &&
          answers[baseKey]?.toLowerCase() === "other"
        ) {
          const otherKey = baseKey.match(/_(xf|cf)$/)
            ? baseKey.replace(/_(xf|cf)$/, "_other_$1")
            : `${baseKey}_other`;
          const alternativeOtherKey = baseKey.match(/_(xf|cf)$/)
            ? baseKey.replace(/_(xf|cf)$/, "Other_$1")
            : `${baseKey}Other`;
          return answers[otherKey] || answers[alternativeOtherKey] || "";
        }
        return answers[baseKey] || "";
      };

      const towerData = {
        dealId: formAnswers.generalInfo?.dealId || location.state?.dealId,
        status: formAnswers.generalInfo?.status,
        towerInfo: {
          towerType: formAnswers.tower?.type,
          scopeOfWork: formAnswers.scopeOfWork || {},
          generalTowerDescription: {
            serialNumber: formAnswers.generalInfo?.serialNumber,
            customer_tower_designation:
              formAnswers.generalInfo?.customer_tower_designation || "",
            submitter: formAnswers.generalInfo?.submitter || "",
            submitter_email: formAnswers.generalInfo?.submitter_email || "",
            statusReason:
              formAnswers.generalInfo?.status === "Rejected"
                ? formAnswers.generalInfo?.statusReason || ""
                : "",
            state: formAnswers.generalInfo?.state || "",
            city: formAnswers.generalInfo?.city || "",
            street: formAnswers.generalInfo?.street || "",
            zip: formAnswers.generalInfo?.zip || "",
            model: formAnswers.generalInfo?.model || "",
            manufacturer:
              getOtherValue(
                formAnswers.towerManufacturer,
                "tower_manufacturer"
              ) || "",
            towerType: formAnswers.tower?.type || "",
            towerSubtype: formAnswers.tower?.subtype || "",
            distribution_system: formAnswers.tower?.distribution_system || "",
            numberOfCells:
              Number(formAnswers.generalInfo?.numberOfCells) || null,
            cellLength: Number(formAnswers.generalInfo?.cellLength) || "",
            cellWidth: Number(formAnswers.generalInfo?.cellWidth) || "",
            cellHeight: Number(formAnswers.generalInfo?.cellHeight) || "",
            cwb_grade_to_curb_height:
              Number(formAnswers.coldWaterBasin?.cwb_grade_to_curb_height) ||
              "",
            baySizeLength:
              getOtherValue(formAnswers.generalInfo, "baySizeLength") || null,
            baySizeWidth:
              getOtherValue(formAnswers.generalInfo, "baySizeWidth") || null,
            baySizeHeight:
              getOtherValue(formAnswers.generalInfo, "baySizeHeight") || null,
            structure_material: formAnswers.structure?.structure_material || "",
            airInletHeight:
              Number(formAnswers.generalInfo?.airInletHeight) || "",
            casing_air_inlets: formAnswers.casing?.casing_air_inlets_cf || "",
            casing_material:
              formAnswers.casing?.casing_material ||
              getOtherValue(formAnswers.casing, "casing_material") ||
              null,
            louver_exists: formAnswers.louvers?.louver_exists || "no",
            louver_blade_width:
              getOtherValue(formAnswers.louvers, "louver_blade_width") || null,
            louver_row_count: formAnswers.louvers?.louver_row_count || null,
            louver_material:
              formAnswers.louvers?.louver_material ||
              getOtherValue(formAnswers.louvers, "louver_material") ||
              null,
            fill_type: getFillTypeValue(),
            dist_header_cl_cwb_curb: Number(
              formAnswers.distributionSystem?.dist_header_cl_cwb_curb || 0
            ),
            motor_hp: Number(formAnswers.mechanical?.motor_hp) || 0,
            fan_diameter:
              getOtherValue(formAnswers.mechanical, "fan_diameter") || 0,
            fanstack_height:
              getOtherValue(formAnswers.fanstack, "fanstack_height") || 0,
            partitionwall_type:
              currentTowerType === "crossflow"
                ? formAnswers.partitionwall?.partitionwall_type_xf || ""
                : formAnswers.partitionwall?.partitionwall_type_cf || "",
            partitionwall_material:
              formAnswers.partitionwall?.partitionwall_material || "",
            partitionwall_frp_type:
              formAnswers.partitionwall?.partitionwall_frp_type || "",
            partitionwall_wood_type:
              formAnswers.partitionwall?.partitionwall_wood_type || "",
            partitionwall_quantity:
              formAnswers.partitionwall?.partitionwall_quantity || "",
            partitionwall_panels_both_sides:
              formAnswers.partitionwall?.partitionwall_panels_both_sides || "",
            partitionwall_between_cells:
              formAnswers.partitionwall?.partitionwall_between_cells || "",
            firewall_exists: formAnswers.firewall?.firewall_exists || "",
            firewall_material: formAnswers.firewall?.firewall_material || "",
            firewall_frp_thickness:
              formAnswers.firewall?.firewall_frp_thickness || "",
            firewall_cement_board_thickness:
              formAnswers.firewall?.firewall_cement_board_thickness || "",
            firewall_between_cells:
              formAnswers.firewall?.firewall_between_cells || "",
            firewall_quantity: formAnswers.firewall?.firewall_quantity || "",
            stairway_count:
              getOtherValue(formAnswers.stairway, "stairway_count") || 0,
            ladder_count:
              getOtherValue(formAnswers.ladders, "ladder_count") || 0,
            tower_notes: formAnswers.notes?.tower_notes || "",
          },
          towerManufacturer: {
            tower_manufacturer:
              formAnswers.towerManufacturer?.tower_manufacturer || "",
            tower_manufacturer_other:
              formAnswers.towerManufacturer?.tower_manufacturer === "other"
                ? formAnswers.towerManufacturer?.tower_manufacturer_other || ""
                : null,
          },
          components: {
            airSeaWall: {},
            casing: {
              ...formAnswers.casing,
              casing_air_inlets: formAnswers.casing?.casing_air_inlets || "",
              casing_air_inlets_cf: formAnswers.casing?.casing_air_inlets || "",
              casing_air_inlets_xf: formAnswers.casing?.casing_air_inlets || "",
              casing_material: formAnswers.casing?.casing_material || "",
              casing_material_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.casing?.casing_material_xf ||
                    formAnswers.casing?.casing_material
                  : "",
              casing_material_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.casing?.casing_material_cf ||
                    formAnswers.casing?.casing_material
                  : "",
              casing_material_other:
                formAnswers.casing?.casing_material === "other"
                  ? formAnswers.casing?.casing_material_other || ""
                  : "",
            },
            coldWaterBasin: {
              ...formAnswers.coldWaterBasin,
              cwb_grade_to_curb_height:
                formAnswers.coldWaterBasin?.cwb_grade_to_curb_height || "",
            },
            distributionSystem: {
              ...formAnswers.distributionSystem,
              dist_header_cl_cwb_curb:
                Number(
                  formAnswers.distributionSystem?.dist_header_cl_cwb_curb
                ) || 0,
              dist_cell_arrangement_cf:
                formAnswers.tower?.distribution_system || "",
            },
            driftEliminators: {},
            ladders: {
              ...formAnswers.ladders,
              ladder_count_other:
                formAnswers.ladders?.ladder_count === "other"
                  ? formAnswers.ladders?.ladder_count_other || ""
                  : null,
            },
            fandeck: {
              ...formAnswers.fandeck,
              fandeck_cell_width: Number(formAnswers.generalInfo?.cellWidth),
            },
            fanstack: {
              ...formAnswers.fanstack,
              fanstack_diameter: formAnswers.mechanical?.fan_diameter,
              fanstack_diameter_other:
                formAnswers.mechanical?.fanstack_diameter === "other"
                  ? formAnswers.mechanical?.fan_diameter_other || ""
                  : null,
              fanstack_height: formAnswers.fanstack?.fanstack_height,
              fanstack_height_other:
                formAnswers.fanstack?.fanstack_height === "other"
                  ? formAnswers.fanstack?.fanstack_height_other || ""
                  : null,
            },
            fill: {
              ...formAnswers.fill,
              fill_type_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.fill?.fill_type_xf
                  : "",
              fill_type_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.fill?.fill_type_cf
                  : "",
              fill_type:
                currentTowerType === "crossflow"
                  ? formAnswers.fill?.fill_type_xf || ""
                  : formAnswers.fill?.fill_type_cf || "",
              fill_type_other_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.fill?.fill_type_other_xf
                  : "",
              fill_type_other_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.fill?.fill_type_cf
                  : "",
              fill_type_other:
                currentTowerType === "crossflow"
                  ? formAnswers.fill?.fill_type_xf === "other"
                    ? formAnswers.fill?.fill_type_other_xf || ""
                    : ""
                  : formAnswers.fill?.fill_type_cf === "other"
                  ? formAnswers.fill?.fill_type_other_cf || ""
                  : "",
            },
            firewall: {
              firewall_exists: formAnswers.firewall?.firewall_exists || "",
              firewall_material: formAnswers.firewall?.firewall_material || "",
              firewall_frp_thickness:
                formAnswers.firewall?.firewall_frp_thickness || "",
              firewall_cement_board_thickness:
                formAnswers.firewall?.firewall_cement_board_thickness || "",
              firewall_between_cells:
                formAnswers.firewall?.firewall_between_cells || "",
              firewall_quantity: formAnswers.firewall?.firewall_quantity || "",
              firewall_material_other:
                formAnswers.firewall?.firewall_material === "other"
                  ? formAnswers.firewall?.firewall_material_other || ""
                  : "",
            },
            hotWaterBasin: {},
            louvers: {
              ...formAnswers.louvers,
              louver_exists:
                currentTowerType === "crossflow"
                  ? formAnswers.louvers?.louver_exists_xf || "no"
                  : formAnswers.louvers?.louver_exists_cf || "no",
              louver_exists_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.louvers?.louver_exists_xf
                  : "",
              louver_exists_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.louvers?.louver_exists_cf
                  : "",
              louver_blade_width:
                currentTowerType === "crossflow" &&
                formAnswers.louvers?.louver_exists_xf === "yes"
                  ? formAnswers.louvers?.louver_blade_width_xf || ""
                  : currentTowerType !== "crossflow" &&
                    formAnswers.louvers?.louver_exists_cf === "yes"
                  ? formAnswers.louvers?.louver_blade_width_cf || ""
                  : "",
              louver_blade_width_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.louvers?.louver_blade_width_xf
                  : "",
              louver_blade_width_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.louvers?.louver_blade_width_cf
                  : "",
              louver_row_count:
                currentTowerType === "crossflow" &&
                formAnswers.louvers?.louver_exists_xf === "yes"
                  ? formAnswers.louvers?.louver_row_count_xf || ""
                  : currentTowerType !== "crossflow" &&
                    formAnswers.louvers?.louver_exists_cf === "yes"
                  ? formAnswers.louvers?.louver_row_count_cf || ""
                  : "",
              louver_row_count_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.louvers?.louver_row_count_xf
                  : "",
              louver_row_count_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.louvers?.louver_row_count_cf
                  : "",
              louver_blade_width_other:
                currentTowerType === "crossflow" &&
                formAnswers.louvers?.louver_exists_xf === "yes"
                  ? formAnswers.louvers?.louver_blade_width_xf === "other"
                    ? formAnswers.louvers?.louver_blade_width_other_xf || ""
                    : ""
                  : currentTowerType !== "crossflow" &&
                    formAnswers.louvers?.louver_exists_cf === "yes"
                  ? formAnswers.louvers?.louver_blade_width_cf === "other"
                    ? formAnswers.louvers?.louver_blade_width_other_cf || ""
                    : ""
                  : "",
              louver_blade_width_other_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.louvers?.louver_blade_width_other_xf
                  : "",
              louver_blade_width_other_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.louvers?.louver_blade_width_other_cf
                  : "",
              louver_material: formAnswers.louvers?.louver_material || "",
              louver_material_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.louvers?.louver_material_xf ||
                    formAnswers.louvers?.louver_material
                  : "",
              louver_material_cf:
                currentTowerType !== "crossflow"
                  ? formAnswers.louvers?.louver_material_cf ||
                    formAnswers.louvers?.louver_material
                  : "",
              louver_material_other:
                formAnswers.louvers?.louver_material === "other"
                  ? formAnswers.louvers?.louver_material_other || ""
                  : "",
            },
            mechanical: {
              ...formAnswers.mechanical,
              motor_hp: Number(formAnswers.mechanical?.motor_hp) || 0,
              fan_diameter: formAnswers.mechanical?.fan_diameter,
              fan_diameter_other:
                formAnswers.mechanical?.fan_diameter === "other"
                  ? formAnswers.mechanical?.fan_diameter_other || ""
                  : null,
            },
            partitionwall: {
              ...formAnswers.partitionwall,
              partitionwall_type:
                currentTowerType === "crossflow"
                  ? formAnswers.partitionwall?.partitionwall_type_xf || ""
                  : formAnswers.partitionwall?.partitionwall_type_cf || "",
              partitionwall_type_cf:
                currentTowerType === "counterflow"
                  ? formAnswers.partitionwall?.partitionwall_type_cf || ""
                  : "",
              partitionwall_type_xf:
                currentTowerType === "crossflow"
                  ? formAnswers.partitionwall?.partitionwall_type_xf || ""
                  : "",
              partitionwall_type_other_cf:
                currentTowerType === "counterflow" &&
                formAnswers.partitionwall?.partitionwall_type_cf === "other"
                  ? formAnswers.partitionwall?.partitionwall_type_other_cf || ""
                  : "",
              partitionwall_type_other_xf:
                currentTowerType === "crossflow" &&
                formAnswers.partitionwall?.partitionwall_type_xf === "other"
                  ? formAnswers.partitionwall?.partitionwall_type_other_xf || ""
                  : "",
              partitionwall_material:
                formAnswers.partitionwall?.partitionwall_material || "",
              partitionwall_material_other:
                formAnswers.partitionwall?.partitionwall_material === "other"
                  ? formAnswers.partitionwall?.partitionwall_material_other ||
                    ""
                  : "",
              partitionwall_frp_type:
                formAnswers.partitionwall?.partitionwall_frp_type || "",
              partitionwall_wood_type:
                formAnswers.partitionwall?.partitionwall_wood_type || "",
              partitionwall_quantity:
                formAnswers.partitionwall?.partitionwall_quantity || "",
              partitionwall_panels_both_sides:
                formAnswers.partitionwall?.partitionwall_panels_both_sides ||
                "",
              partitionwall_between_cells:
                formAnswers.partitionwall?.partitionwall_between_cells || "",
            },
            stairway: {
              ...formAnswers.stairway,
              stairway_count_other:
                formAnswers.stairway?.stairway_count === "other"
                  ? formAnswers.stairway?.stairway_count_other || ""
                  : null,
            },
            structure: formAnswers.structure || {},
            walkways: {},
            windwall: {},
            notes: {
              ...formAnswers.notes,
              tower_notes: formAnswers.notes?.tower_notes || "",
            },
            fakefordatamatch: {
              model: formAnswers.generalInfo?.model || "",
              tower_manufacturer:
                formAnswers.towerManufacturer?.tower_manufacturer || "",
              towerSubtype: formAnswers.tower?.subtype || "",
              cellLength: Number(formAnswers.generalInfo?.cellLength) || "",
              cellWidth: Number(formAnswers.generalInfo?.cellWidth) || "",
              cellHeight: Number(formAnswers.generalInfo?.cellHeight) || "",
              baySizeLength: formAnswers.generalInfo?.baySizeLength || "",
              baySizeLengthOther:
                formAnswers.generalInfo?.baySizeLengthOther || "",
              baySizeWidth: formAnswers.generalInfo?.baySizeWidth || "",
              baySizeWidthOther:
                formAnswers.generalInfo?.baySizeWidthOther || "",
              baySizeHeight: formAnswers.generalInfo?.baySizeHeight || "",
              baySizeHeightOther:
                formAnswers.generalInfo?.baySizeHeightOther || "",
              numberOfCells:
                Number(formAnswers.generalInfo?.numberOfCells) || null,
              airInletHeight:
                Number(formAnswers.generalInfo?.airInletHeight) || "",
            },
          },
          companyInfo: {
            company_name: formAnswers.companyInfo?.company_name || "",
          },
        },
      };

      let mergedData = {
        ...initialData,
        dealId: towerData.dealId || initialData.dealId,
        status: towerData.status || initialData.status,
        towerInfo: {
          ...initialData.towerInfo,
          ...towerData.towerInfo,
          generalTowerDescription: {
            ...initialData.towerInfo?.generalTowerDescription,
            ...towerData.towerInfo.generalTowerDescription,
          },
          components: {
            ...initialData.towerInfo?.components,
            ...towerData.towerInfo.components,
            casing: {
              ...initialData.towerInfo?.components?.casing,
              ...towerData.towerInfo.components.casing,
            },
            coldWaterBasin: {
              ...initialData.towerInfo?.components?.coldWaterBasin,
              ...towerData.towerInfo.components.coldWaterBasin,
            },
            distributionSystem: {
              ...initialData.towerInfo?.components?.distributionSystem,
              ...towerData.towerInfo.components.distributionSystem,
            },
            ladders: {
              ...initialData.towerInfo?.components?.ladders,
              ...towerData.towerInfo.components.ladders,
            },
            fandeck: {
              ...initialData.towerInfo?.components?.fandeck,
              ...towerData.towerInfo.components.fandeck,
            },
            fanstack: {
              ...initialData.towerInfo?.components?.fanstack,
              ...towerData.towerInfo.components.fanstack,
            },
            fill: {
              ...initialData.towerInfo?.components?.fill,
              ...towerData.towerInfo.components.fill,
            },
            louvers: {
              ...initialData.towerInfo?.components?.louvers,
              ...towerData.towerInfo.components.louvers,
            },
            mechanical: {
              ...initialData.towerInfo?.components?.mechanical,
              ...towerData.towerInfo.components.mechanical,
            },
            stairway: {
              ...initialData.towerInfo?.components?.stairway,
              ...towerData.towerInfo.components.stairway,
            },
            structure: {
              ...initialData.towerInfo?.components?.structure,
              ...towerData.towerInfo.components.structure,
            },
            notes: {
              ...initialData.towerInfo?.components?.notes,
              ...towerData.towerInfo.components.notes,
            },
          },
          scopeOfWork: initialData.towerInfo?.scopeOfWork || {},
          companyInfo: {
            ...initialData.towerInfo?.companyInfo,
            ...towerData.towerInfo.companyInfo,
          },
        },
      };

      towerData.towerInfo.components.fill = removeEmptyValues(
        towerData.towerInfo.components.fill
      );
      mergedData.towerInfo.components.fill = removeEmptyValues(
        mergedData.towerInfo.components.fill
      );

      towerData.towerInfo.components.ladders = removeEmptyValues(
        towerData.towerInfo.components.ladders
      );
      mergedData.towerInfo.components.ladders = removeEmptyValues(
        mergedData.towerInfo.components.ladders
      );

      towerData.towerInfo.components.louvers = removeEmptyValues(
        towerData.towerInfo.components.louvers
      );
      mergedData.towerInfo.components.louvers = removeEmptyValues(
        mergedData.towerInfo.components.louvers
      );

      towerData.towerInfo.components.stairway = removeEmptyValues(
        towerData.towerInfo.components.stairway
      );
      mergedData.towerInfo.components.stairway = removeEmptyValues(
        mergedData.towerInfo.components.stairway
      );

      towerData.towerInfo.components.mechanical = removeEmptyValues(
        towerData.towerInfo.components.mechanical
      );
      mergedData.towerInfo.components.mechanical = removeEmptyValues(
        mergedData.towerInfo.components.mechanical
      );

      towerData.towerInfo.components.fanstack = removeEmptyValues(
        towerData.towerInfo.components.fanstack
      );
      mergedData.towerInfo.components.fanstack = removeEmptyValues(
        mergedData.towerInfo.components.fanstack
      );

      towerData.towerInfo.components.partitionwall = removeEmptyValues(
        towerData.towerInfo.components.partitionwall
      );
      mergedData.towerInfo.components.partitionwall = removeEmptyValues(
        mergedData.towerInfo.components.partitionwall
      );

      towerData.towerInfo.towerManufacturer = removeEmptyValues(
        towerData.towerInfo.towerManufacturer
      );
      mergedData.towerInfo.towerManufacturer = removeEmptyValues(
        mergedData.towerInfo.towerManufacturer
      );

      console.log(
        "PREMerged data:",
        JSON.parse(JSON.stringify(towerData)),
        JSON.parse(JSON.stringify(mergedData)),
        JSON.parse(JSON.stringify(initialData))
      );

      //this is a very specific use case, below is a more dynamic one
      // if (towerData.towerInfo.generalTowerDescription.fanstack_height) {
      //   updateMatchingFields(
      //     mergedData.towerInfo,
      //     'generalTowerDescription.fanstack_height',
      //     towerData.towerInfo.generalTowerDescription.fanstack_height
      //   );
      // }

      //automatically update matching fields based on form answers
      const updateAllMatchingFields = (mergedData) => {
        // Process components section

        Object.entries(mergedData.towerInfo.components).forEach(
          ([section, sectionData]) => {
            if (typeof sectionData === "object") {
              Object.entries(sectionData).forEach(([field, value]) => {
                if (field.includes("ladder_count")) return;
                //console.log("field", field, value);

                let fieldPath = `components.${section}.${field}`;

                // Special handling for fill_type fields
                if (field.includes("fill_type")) {
                  const componentSection =
                    mergedData.towerInfo.components[section];
                  if (componentSection && value === "other") {
                    componentSection["fill_type"] = "other";
                    componentSection["fill_type_xf"] = "other";
                    componentSection["fill_type_cf"] = "other";
                  }
                }

                // Find matching field group and handle other fields
                const matchingGroup = matchingFieldsGTD.find((group) =>
                  group.some((path) => path === fieldPath)
                );

                if (matchingGroup && value === "other") {
                  matchingGroup.forEach((path) => {
                    const pathParts = path.split(".");
                    if (!path.includes("_other")) {
                      let current = mergedData.towerInfo;
                      for (let i = 0; i < pathParts.length - 1; i++) {
                        current = current[pathParts[i]] =
                          current[pathParts[i]] || {};
                      }
                      current[pathParts[pathParts.length - 1]] = "other";
                    }
                  });
                }

                //is this field in the ignore list?
                if (
                  matchingFieldsGTD_Ignore &&
                  matchingFieldsGTD_Ignore.includes(fieldPath)
                ) {
                  return;
                }

                //is this field in matchingFieldsGTD?
                const existsInMatchingFields = matchingFieldsGTD.some((group) =>
                  group.includes(fieldPath)
                );

                if (existsInMatchingFields) {
                  //special handling for GTD fields
                  updateMatchingFields(
                    mergedData.towerInfo,
                    fieldPath,
                    value,
                    matchingFieldsGTD
                  );
                } else {
                  updateMatchingFields(
                    mergedData.towerInfo,
                    fieldPath,
                    value,
                    matchingFields
                  );
                }
              });
            }
          }
        );
        /*
        // Process generalTowerDescription section
        if (mergedData.towerInfo.generalTowerDescription) {
          Object.entries(mergedData.towerInfo.generalTowerDescription).forEach(([field, value]) => {
            let fieldPath = `generalTowerDescription.${field}`;
            
            // Find matching field group and handle other fields
            const matchingGroup = matchingFieldsGTD.find(group => 
              group.some(path => path === fieldPath)
            );
      
            if (matchingGroup && value === 'other') {
              matchingGroup.forEach(path => {
                const pathParts = path.split('.');
                if (!path.includes('_other')) {
                  let current = mergedData.towerInfo;
                  for (let i = 0; i < pathParts.length - 1; i++) {
                    current = current[pathParts[i]] = current[pathParts[i]] || {};
                  }
                  current[pathParts[pathParts.length - 1]] = 'other';
                }
              });
            }
      
            // Update matching fields as normal
            updateMatchingFields(
              mergedData.towerInfo,
              fieldPath,
              value,
              matchingFieldsGTD
            );
          });
        }
        */
        return mergedData;
      };

      updateAllMatchingFields(mergedData);
      console.log(
        "Merged data:",
        JSON.parse(JSON.stringify(towerData)),
        JSON.parse(JSON.stringify(mergedData))
      );

      if (formAnswers.generalInfo.status !== previousStatus) {
        const submitterEmail =
          formAnswers.generalInfo?.submitter_email ||
          formAnswers.generalInfo?.submitter;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const angleEmailRegex = /^[^<>]+\s*<[^\s@]+@[^\s@]+\.[^\s@]+>$/;

        if (
          emailRegex.test(submitterEmail) ||
          angleEmailRegex.test(submitterEmail)
        ) {
          apiService
            .post("/tower/status-change-email", {
              to: submitterEmail,
              status: formAnswers.generalInfo.status,
              towerDetails: {
                serialNumber: formAnswers.generalInfo?.serialNumber,
              },
            })
            .catch((error) => {
              console.log("Email sending failed:", error);
            });
        }
      }

      let response;
      if (isUpdateMode && location.state.initialData) {
        response = await apiService.post(
          `/tower/createOrUpdate/${location.state._id}`,
          mergedData
        );
        if (response.status === 200) {
          addToast("Tower Updated Successfully!");
          navigate("/dashboard");
        } else if (response.status === 201) {
          addToast("Tower Created Successfully!");
          navigate("/dashboard");
        }
      } else {
        response = await apiService.post("/tower/createNewTower", mergedData);
        if (response.status === 201) {
          addToast("Tower Created Successfully!");
          navigate("/dashboard");
        }
      }
    } catch (error) {
      addToast(error.response?.data?.message || MESSAGE.SERVER_ERROR, "danger");
      console.log("Error:", error);
    } finally {
      setIsSubmitting(false);
      setHasWarningShown(false);
    }
  };

  const getFieldClassName = (fieldPath) => {
    return `form-field ${
      validationAttempted && invalidFields.includes(fieldPath)
        ? "invalid-field"
        : ""
    }`;
  };

  const handleAnswerChange = (componentId, answers) => {
    console.log("Received answers:", componentId, answers);

    setFormAnswers((prevState) => {
      const newState = { ...prevState };

      // If tower type changed and reset flag is set, reset distribution system
      if (componentId === "towerType" && answers._resetDistributionSystem) {
        newState.tower = {
          ...newState.tower,
          ...answers,
          distribution_system: "",
        };

        // Also reset distribution system component if it exists
        if (newState.distributionSystem) {
          newState.distributionSystem = {
            ...newState.distributionSystem,
            dist_cell_arrangement_cf: "",
          };
        }

        return newState;
      }

      // Handle subtype changes - reset distribution system if subtype changes
      if (
        componentId === "tower" &&
        answers.subtype !== prevState.tower?.subtype
      ) {
        newState.tower = {
          ...newState.tower,
          ...answers,
          distribution_system: "",
        };

        if (newState.distributionSystem) {
          newState.distributionSystem = {
            ...newState.distributionSystem,
            dist_cell_arrangement_cf: "",
          };
        }

        return newState;
      }

      newState[componentId] = {
        ...newState[componentId],
        ...answers,
      };

      return newState;
    });
  };

  const handleCreateNewSerialNumber = async () => {
    if (!existingSerialNumber) {
      setShowSerialNumberModal(false);
      setEdit(true);
    }
  };

  return (
    <div className="create-project-wrapper">
      <div className="create-project-container">
        <form onSubmit={handleSubmit}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1>{isUpdateMode ? "Update Tower" : "Create New Tower"}</h1>
            {!existingSerialNumber && (
              <Button
                variant="primary"
                onClick={() => setShowSerialNumberModal(true)}
                className="align-self-end"
              >
                Generate Serial Number
              </Button>
            )}
          </div>
          {showSubtype &&
            Object.keys(PROJECT_FORM_MAPPING).map((section) =>
              PROJECT_FORM_MAPPING[section].map((mapping) => {
                const t_questionId =
                  currentTowerType && Array.isArray(mapping.questionId)
                    ? mapping.questionId.find(
                        (q) => q.towerType === currentTowerType
                      ).id
                    : mapping.questionId;

                return (
                  <DynamicForm
                    key={`${mapping.componentId}_${
                      typeof t_questionId === "object"
                        ? JSON.stringify(t_questionId)
                        : t_questionId
                    }_${currentTowerType}`}
                    componentId={mapping.componentId}
                    questionId={t_questionId}
                    unit={mapping.unit || ""}
                    initialValue={
                      formAnswers[mapping.componentId]?.[t_questionId] || ""
                    }
                    onAnswerChange={(answers) => {
                      handleAnswerChange(mapping.componentId, answers);
                    }}
                    formAnswers={formAnswers}
                    className={getFieldClassName(
                      `${mapping.componentId}.${t_questionId}`
                    )}
                  />
                );
              })
            )}

          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : isUpdateMode
                ? "Update Tower"
                : "Save Tower"}
            </button>
          </div>
        </form>
      </div>
      <SerialNumberModal
        show={showSerialNumberModal}
        onHide={() => setShowSerialNumberModal(false)}
        onConfirm={handleCreateNewSerialNumber}
      />

      <EditNumber
        show={edit}
        onHide={() => setEdit(false)}
        onConfirm={() => {}}
        setEdit={setEdit}
        setSerialNumber={setSerialNumber}
        parentAnswers={formAnswers}
        setParentAnswers={setFormAnswers}
      />
    </div>
  );
};

export default CreateTowerPage;
